import { type FormikProps } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loading from '@components/Shared/Loader/Loading';
import useGetCustomersList from '@hooks/NTX/Peoples/useGetCustomersList';
import useGetReceiptsTypeList from '@hooks/Finance/ArReceipts/useGetReceiptsTypeList';
import { useState } from 'react';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import { Card } from '@components/card/card';
import { receiptTypeEnum } from '../receiptTypeEnum';

import { StandardReceiptComponent } from './StandardReceiptComponent';
import { RefundReceiptComponent } from './RefundReceiptComponent';
import { PrePaymentReceiptComponent } from './PrePaymentReceiptComponent';
import { ApplyDebitMemoComponent } from './ApplyDebitMemoComponent';
import { ApplyPreReceiptComponent } from './ApplyPreReceiptComponent';

interface Props {
  formik: FormikProps<any>;
  onClose: () => void;
}

const ChooseType: React.FC<Props> = ({ formik, onClose }) => {
  const { values } = formik;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { OperatingUnitOptions, loading: loadingOU, error: errorOU } = useGetOperatingUnitWithPermission();
  const { treeCustomerData, error: errorCustomers, loading: loadingCustomers } = useGetCustomersList();
  const { ReceiptsTypeList } = useGetReceiptsTypeList();

  const [selectedPaymentType, setSelectedPaymentType] = useState<any>(null);

  const getPaymentTypeIcon = (type: any) => {
    switch (type) {
      case receiptTypeEnum.Standard:
        return <i className='fas fa-file-invoice fa-2x text-success' title='سند قبض عادي'></i>;
      case receiptTypeEnum.Refund:
        return <i className='fas fa-rotate-left fa-2x text-warning' title='استرداد'></i>;
      case receiptTypeEnum.PreReceipt:
        return <i className='fas fa-hand-holding-usd fa-2x text-primary' title='سند قبض مسبق'></i>;
      case receiptTypeEnum.ApplyDebitMemo:
        return <i className='fas fa-file-invoice-dollar fa-2x text-info' title='تطبيق إشعار دائن'></i>;
      case receiptTypeEnum.ApplyPreReceipt:
        return <i className='fas fa-money-check fa-2x text-purple' title='تطبيق دفعة مسبقة'></i>;
      default:
        return <i className='fas fa-file-invoice fa-2x text-secondary'></i>;
    }
  };

  const getReceiptTypeDescription = (type: any) => {
    switch (type) {
      case receiptTypeEnum.Standard:
        // return 'تسوية فواتير مستحقة';
        return '';
      case receiptTypeEnum.Refund:
        // return 'إرجاع مبالغ زائدة';
        return '';
      case receiptTypeEnum.PreReceipt:
        // return 'تسجيل دفعة بدون فاتورة';
        return '';
      case receiptTypeEnum.ApplyDebitMemo:
        // return 'استخدم رصيد المرتجع للتسوية';
        return '';
      case receiptTypeEnum.ApplyPreReceipt:
        // return 'وزّع دفعة سابقة على فواتير';
        return '';
      default:
        return '';
    }
  };

  const handleReceiptTypeSelect = (ReceiptType: any) => {
    setSelectedPaymentType(ReceiptType);
    formik.setFieldValue('receiptType', ReceiptType.id);
    // Reset related fields
    formik.setFieldValue('amount', 0);
    formik.setFieldValue('scheduleList', []);
  };

  const handleBackToSelection = () => {
    setSelectedPaymentType(null);
    formik.setFieldValue('paymentType', null);
  };

  const handleClose = () => {
    navigate('/AR/ArReceipts', { replace: true });
  };

  return (
    <>
      <div className='p-3  min-vh-75s'>
        {/* Header Section */}
        <div className='d-flex justify-content-between align-items-center  border-bottom pb-3'>
          <div className='d-flex align-items-center'>
            <div className='bg-theme bg-opacity-10 rounded-3 p-3 me-3'>
              <i className='fas fa-money-bill-wave fa-lg   text-theme'></i>
            </div>
            <div>
              <h4 className='mb-1 text-theme'>إضافة سند قبض جديـد</h4>
              <p className='text-muted small mb-0'>اختر نوع المعاملة المالية المناسبة</p>
            </div>
          </div>
          <button type='button' className='btn-close' onClick={handleClose} aria-label='Close' title='إغلاق'></button>
        </div>
        <Loading loading={loadingCustomers || loadingOU} error={errorCustomers || errorOU} Type='Dots'>
          {!selectedPaymentType ? (
            // Step 1: Payment Type Selection
            <div className='row g-3 my-3'>
              <div className='col-lg-12'>
                <div className='text-center mb-4'>
                  <h5 className='fw-bold text-theme mb-2'>اختر نوع المعاملة المالية</h5>
                  <p className='text-muted mb-0'>حدد نوع السند الذي تريد إنشاءه من الخيارات المتاحة أدناه</p>
                </div>

                <div className='form-group'>
                  <div className='d-flex flex-wrap gap-3 justify-content-center'>
                    {ReceiptsTypeList?.filter(f => f.id !== receiptTypeEnum.Offset)?.map((item, idx) => (
                      <div key={idx} className='flex-grow-1' style={{ minWidth: '160px', maxWidth: '200px' }}>
                        <Card className={`h-100 customcard    transition-all`}>
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleReceiptTypeSelect(item)}
                            role='button'
                            tabIndex={0}
                            onKeyPress={e => e.key === 'Enter' && handleReceiptTypeSelect(item)}
                          >
                            <div className='card-body text-center p-3 d-flex flex-column'>
                              <div className='mb-3 flex-grow-1 d-flex align-items-center justify-content-center'>
                                {getPaymentTypeIcon(item.id)}
                              </div>
                              <h6 className='card-title mb-2 fw-bold' style={{ fontSize: '0.9rem' }}>
                                {i18n.language === 'en' ? item.name : item.name2}
                              </h6>
                              <p
                                className='card-text small text-muted mb-3 flex-grow-1'
                                style={{ fontSize: '0.75rem' }}
                              >
                                {getReceiptTypeDescription(item.id)}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </div>
                    ))}
                  </div>

                  {/* Empty State */}
                  {ReceiptsTypeList?.filter(f => f.id !== receiptTypeEnum.Quick && f.id !== receiptTypeEnum.Offset)
                    ?.length === 0 && (
                    <div className='text-center py-5'>
                      <i className='fas fa-money-bill-wave fa-3x text-muted mb-3'></i>
                      <h5 className='text-muted mb-2'>لا توجد أنواع سندات قبض متاحة</h5>
                      <p className='text-muted'>يرجى التحقق من الصلاحيات أو إعدادات النظام</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Step 2: Show Specific Component Based on Selected Type
            <div>
              <div className='d-flex justify-content-between align-items-center mb-4 mt-2 p-2 shadow-sm rounded'>
                <button className='btn btn-outline-secondary btn-sm' onClick={handleBackToSelection}>
                  <i className='fas fa-arrow-right me-2'></i>
                  العودة لاختيار نوع السند
                </button>

                <div className='d-flex align-items-center bg-theme bg-opacity-10 px-3 py-2 rounded'>
                  <div className='me-3'>{getPaymentTypeIcon(selectedPaymentType.id)}</div>
                  <div>
                    <h6 className='mb-0 text-theme fw-bold'>
                      {i18n.language === 'en' ? selectedPaymentType.name : selectedPaymentType.name2}
                    </h6>
                    <small className='text-muted'>{getReceiptTypeDescription(selectedPaymentType.id)}</small>
                  </div>
                </div>
              </div>

              <div className=''>
                {selectedPaymentType.id === receiptTypeEnum.Standard && (
                  <StandardReceiptComponent
                    formik={formik}
                    onClose={onClose}
                    OperatingUnitOptions={OperatingUnitOptions}
                    treeCustomerData={treeCustomerData}
                  />
                )}
                {selectedPaymentType.id === receiptTypeEnum.Refund && (
                  <RefundReceiptComponent
                    formik={formik}
                    onClose={onClose}
                    OperatingUnitOptions={OperatingUnitOptions}
                    treeCustomerData={treeCustomerData}
                  />
                )}
                {selectedPaymentType.id === receiptTypeEnum.PreReceipt && (
                  <PrePaymentReceiptComponent
                    formik={formik}
                    onClose={onClose}
                    OperatingUnitOptions={OperatingUnitOptions}
                    treeCustomerData={treeCustomerData}
                  />
                )}
                {selectedPaymentType.id === receiptTypeEnum.ApplyCreditMemo && (
                  <ApplyDebitMemoComponent
                    formik={formik}
                    onClose={onClose}
                    OperatingUnitOptions={OperatingUnitOptions}
                    treeCustomerData={treeCustomerData}
                  />
                )}
                {selectedPaymentType.id === receiptTypeEnum.ApplyPreReceipt && (
                  <ApplyPreReceiptComponent
                    formik={formik}
                    onClose={onClose}
                    OperatingUnitOptions={OperatingUnitOptions}
                    treeCustomerData={treeCustomerData}
                  />
                )}
              </div>
            </div>
          )}
        </Loading>
      </div>
      <style>{`
      .transition-all {
        transition: all 0.3s ease;
      }
      .min-vh-50 {
        min-height: 50vh;
      }
      .cursor-pointer {
        cursor: pointer;
      }
      .customcard:hover {
        transform: translateY(-2px);
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
      }
    `}</style>
    </>
  );
};
export default ChooseType;
